<script lang="ts">
  import mobile from "is-mobile";

  import DesktopApp from "./DesktopApp.svelte";
  import MobileApp from "./MobileApp.svelte";
  import {
    initializeSounds,
    playMusic,
    soundsLoaded,
    suppressSounds,
  } from "./utils/AudioManager";
  import { appStarted, assetsLoaded, CurrentDay } from "./store/AppState";
  import { generateConfig } from "./utils/Activities";
  import { giftsConfig } from "./config/gifts";
  import type { Config } from "./declarations/config";
  import {
    activitiesConfig,
    urlParams,
    selectedActivityIndex,
    selectedOtherActivity,
    isOtherActivitiesWindowOpen,
    backgroundLoaded,
  } from "./store/AppState";
  import { otherActivities } from "./config/other-activities";
  import Loader from "./components/Loader.svelte";
  import { sendStartEvent } from "./utils/analytics";
  import { onMount } from "svelte";

  const isMobile = mobile();

  let orientation = screen.orientation.type;
  window.addEventListener("orientationchange", () => {
    orientation = screen.orientation.type;
    buttonClicked = false;
  });

  window.addEventListener("blur", () => {
    const shadowRoot = document.activeElement?.shadowRoot;
    const activeElement = shadowRoot?.activeElement as HTMLIFrameElement;

    if (activeElement?.contentWindow?.document.hasFocus()) {
      return;
    } else {
      suppressSounds(true);
    }
  });

  window.addEventListener("focus", () => {
    const activity = $urlParams.get("aktivita");
    if (!activity || ["soutez", "o-projektu"].includes(activity)) {
      suppressSounds(false);
    }
  });

  const setAppMode = () => {
    return isMobile || window.innerWidth < 640;
  };

  $: displayMobile = setAppMode();

  window.addEventListener("orientationchange", () => {
    displayMobile = setAppMode();
  });
  window.addEventListener("resize", () => {
    displayMobile = setAppMode();
  });

  const soundIndices = [...Array(24).keys()];
  const soundFiles = soundIndices.map(
    (index) => `${__PUBLIC_PATH__}audio/${index + 1}.mp3`
  );
  const dialogueIndices = ["3", "4", "6", "12", "13", "14", "16", "22"];
  const dialogueFiles: string[] = [];
  dialogueIndices.forEach((value) => {
    dialogueFiles.push(`${__PUBLIC_PATH__}audio/${value}_1.mp3`);
    dialogueFiles.push(`${__PUBLIC_PATH__}audio/${value}_2.mp3`);
    dialogueFiles.push(`${__PUBLIC_PATH__}audio/${value}_3.mp3`);
  });

  let activitiesInitialized = false;
  const initializeActivities = () => {
    if (__MODE__ !== "production" || (window.DP && window.DP.isReady())) {
      if (window.DP && window.DP.isProduction && window.DP.isProduction()) {
        const url = new URL(window.location.href);
        if (url.searchParams.has("fakeNow")) {
          url.searchParams.delete("fakeNow");
          window.location.replace(url.toString());
          return;
        }
      }
      $activitiesConfig = [];
      giftsConfig.forEach((gift: Config) => {
        $activitiesConfig.push(generateConfig(gift));
      });

      if ($urlParams.has("aktivita")) {
        const gift = giftsConfig.find(
          (activity) => activity.slug === $urlParams.get("aktivita")
        );
        if (gift && gift.order) {
          $selectedActivityIndex = gift.order - 1;
        } else {
          const otherActivity = otherActivities.find(
            (otherActivity) => otherActivity.slug === $urlParams.get("aktivita")
          );
          if (otherActivity && otherActivity.order) {
            $selectedOtherActivity = otherActivity.order;
            $isOtherActivitiesWindowOpen = true;
          }
        }
      }
      activitiesInitialized = true;
    } else {
      setTimeout(() => initializeActivities(), 1000);
    }
  };

  let BackgroundLoader: HTMLElement;
  let LandingPage: HTMLElement;
  let buttonClicked: boolean = false;

  const handlePageSwitch = () => {
    if (
      buttonClicked &&
      $assetsLoaded &&
      preloadedContent &&
      (isMobile || $backgroundLoaded)
    ) {
      initializeActivities();
      if (!activitiesInitialized) {
        setTimeout(() => handlePageSwitch(), 1000);
        return;
      } else {
        BackgroundLoader.classList.remove("hidden");
        LandingPage.classList.add("hidden");
        sendStartEvent();
      }
    }
  };

  let musicStarted = false;
  const handleClick = () => {
    buttonClicked = true;
    $appStarted = true;
    handlePageSwitch();
    if ($soundsLoaded && !musicStarted) {
      playMusic();
      musicStarted = true;
    }
  };

  assetsLoaded.subscribe(() => {
    handlePageSwitch();
    if ($assetsLoaded) {
      initializeSounds([
        ...soundFiles,
        `${__PUBLIC_PATH__}audio/music.mp3`,
        ...dialogueFiles,
        `${__PUBLIC_PATH__}audio/closed_1.mp3`,
        `${__PUBLIC_PATH__}audio/closed_2.mp3`,
        `${__PUBLIC_PATH__}audio/closed_3.mp3`,
        `${__PUBLIC_PATH__}audio/closed_4.mp3`,
      ]);
    }
  });

  let preloadCounter = 0;
  let preloadedContent = false;

  const handlePreloads = () => {
    preloadCounter++;
    if (preloadCounter === 5) {
      preloadedContent = true;
      handlePageSwitch();
    }
  };

  onMount(() => {
    if (window.isClosed) {
      const url = new URL(window.location.href);
      if (url.searchParams.has("aktivita")) {
        url.searchParams.delete("aktivita");
        window.location.replace(url.toString());
        return;
      }
    }
    const hash = window.location.hash;
    if (
      hash.includes("vanoce-a-pohadky") &&
      !window.location.search.includes("aktivita=vanoce-a-pohadky-v-az-kvizu")
    ) {
      let redirectURL;
      if (window.location.search) {
        redirectURL =
          window.location.origin +
          window.location.pathname +
          window.location.search +
          "&aktivita=vanoce-a-pohadky-v-az-kvizu" +
          window.location.hash;
      } else {
        redirectURL =
          window.location.origin +
          window.location.pathname +
          "?aktivita=vanoce-a-pohadky-v-az-kvizu" +
          window.location.hash;
      }
      window.location.replace(redirectURL);
    }
  });
</script>

<link
  on:load={handlePreloads}
  rel="preload"
  href={`${__PUBLIC_PATH__}images/logo.svg`}
  as="image"
/>
<link
  on:load={handlePreloads}
  rel="preload"
  href={`${__PUBLIC_PATH__}images/motiv.jpg`}
  as="image"
/>
<link
  on:load={handlePreloads}
  rel="preload"
  href={`${__PUBLIC_PATH__}images/darky.svg`}
  as="image"
/>
<link
  on:load={handlePreloads}
  rel="preload"
  href={`${__PUBLIC_PATH__}images/preloader.svg`}
  as="image"
/>
<link
  on:load={handlePreloads}
  rel="preload"
  href={`${__PUBLIC_PATH__}images/oblacek.svg`}
  as="image"
/>
{#if orientation.includes("landscape") && (screen.height < 500 || isMobile) && !$urlParams.has("aktivita")}
  <div
    class="not-supported-container"
    style={`height: ${
      __MODE__ === "production" ? "calc(100dvh - 6.4rem)" : "100vh"
    };`}
  >
    <p>
      Rozměry Vašeho displeje nejsou podporované. Zkuste Vaše zařízení otočit na
      výšku.
    </p>
  </div>
{:else}
  <div
    bind:this={LandingPage}
    class="landing-page"
    style={`--height: ${
      __MODE__ === "production" ? "calc(100dvh - 6.4rem)" : "100dvh"
    }`}
    on:pointerup={handleClick}
  >
    <Loader {buttonClicked} />
    <div class="logo-text-wrapper">
      {#if !buttonClicked}
        <img
          class="logo"
          style={`--width: ${isMobile ? "80dvw" : "auto"}`}
          alt="logo"
          src={`${__PUBLIC_PATH__}images/logo.svg`}
        />
        <article class="landing-text">
          {#if !window.isClosed || window.isShowroom}
            <p>
              Letos pro tebe adventní kalendář podle Pržina plánku postavila <strong
                >celá parta z Déčka</strong
              >.
            </p>
            <p>
              Každý den až do Ježíška si tu můžeš rozbalit jeden dárek s hrou,
              pohádkou nebo třeba tvořením.
            </p>
            <p><strong>Moc si to užij!</strong></p>
          {:else}
            <p>
              V roce 2024 postavila adventní kalendář podle Pržina plánku <strong
                >celá parta z Déčka</strong
              >.
            </p>{/if}

          <button class="open-button">
            {#if !window.isClosed || window.isShowroom}
              Začít
            {:else}
              Spustit
            {/if}
          </button>
        </article>
      {/if}
    </div>
    <div class="backdrop" />
    <img class="motiv" alt="logo" src={`${__PUBLIC_PATH__}images/motiv.jpg`} />
    <img class="darky" alt="logo" src={`${__PUBLIC_PATH__}images/darky.svg`} />
  </div>
  {#if $CurrentDay !== 0}
    <div bind:this={BackgroundLoader} class="hidden">
      {#if displayMobile}
        <MobileApp />
      {/if}
      {#if !displayMobile}
        <DesktopApp />
      {/if}
    </div>
  {/if}
{/if}

<style>
  .landing-page {
    width: 100%;
    height: var(--height);
    min-height: var(--height);
    position: absolute;
    overflow: hidden;
    cursor: pointer;
  }

  .landing-page * {
    cursor: pointer;
  }

  .motiv {
    height: 100%;
    width: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .darky {
    object-fit: cover;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
  }

  .backdrop {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
  }

  .logo-text-wrapper {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .logo {
    width: var(--width);
  }

  .landing-text {
    font-family: "source-sans-pro", sans-serif;
    font-size: max(1.3125rem, 2.7vh);
    font-style: normal;
    font-weight: 400;
    line-height: max(1.75rem, 3vh);
    color: hsla(210, 61%, 29%, 1);
    margin-top: 1rem;
  }

  .open-button {
    background-color: hsla(19, 88%, 54%, 1);
    border: none;
    color: hsla(0, 0%, 100%, 1);
    height: 7.5rem;
    width: 15rem;
    padding: 0rem 2.5rem;
    border-radius: 0.375rem;

    color: var(--white, #fff);
    text-align: center;
    font-family: "source-sans-pro", sans-serif;
    font-size: 3rem;
    font-style: normal;
    font-weight: 700;
    line-height: 3.75rem;
  }

  .hidden {
    visibility: hidden !important;
  }

  :global(.hidden *) {
    visibility: hidden !important;
  }

  .not-supported-container {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-family: "source-sans-pro", sans-serif;
  }

  .not-supported-container p {
    margin: 0;
    font-size: 2rem;
  }
</style>
